import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { XeroInvoice } from '../../../types/xero';
import { parseXeroDate } from '../../../utils/xeroDate';

interface TopCustomersViewProps {
  invoices: XeroInvoice[];
  startDate: Date;
  endDate: Date;
}

interface CustomerRevenue {
  name: string;
  amount: number;
  invoiceCount: number;
  pct: number;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);

export function TopCustomersView({ invoices, startDate, endDate }: TopCustomersViewProps) {
  const { customers, totalRevenue, uniqueCustomerCount } = useMemo(() => {
    const revenueMap: Record<string, { amount: number; count: number }> = {};

    invoices.forEach((inv) => {
      if (inv.Status === 'VOIDED') return;
      const contactName = inv.Contact?.Name;
      if (!contactName || inv.AmountPaid <= 0) return;

      if (inv.Payments && inv.Payments.length > 0) {
        inv.Payments.forEach(p => {
          const payDate = parseXeroDate(p.Date);
          if (payDate && payDate >= startDate && payDate <= endDate) {
            if (!revenueMap[contactName]) revenueMap[contactName] = { amount: 0, count: 0 };
            revenueMap[contactName].amount += p.Amount;
            revenueMap[contactName].count++;
          }
        });
      } else {
        const invDate = parseXeroDate((inv as any).Date || (inv as any).DateString);
        if (invDate && invDate >= startDate && invDate <= endDate) {
          if (!revenueMap[contactName]) revenueMap[contactName] = { amount: 0, count: 0 };
          revenueMap[contactName].amount += inv.AmountPaid;
          revenueMap[contactName].count++;
        }
      }
    });

    const total = Object.values(revenueMap).reduce((s, v) => s + v.amount, 0);
    const sorted: CustomerRevenue[] = Object.entries(revenueMap)
      .map(([name, data]) => ({
        name,
        amount: data.amount,
        invoiceCount: data.count,
        pct: total > 0 ? (data.amount / total) * 100 : 0,
      }))
      .sort((a, b) => b.amount - a.amount);

    return { customers: sorted, totalRevenue: total, uniqueCustomerCount: sorted.length };
  }, [invoices, startDate, endDate]);

  const topN = customers.slice(0, 8);
  const otherAmount = customers.slice(8).reduce((s, c) => s + c.amount, 0);
  const topCustomer = customers[0];

  const chartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
      fontFamily: 'inherit',
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 4,
        barHeight: '65%',
        distributed: true,
      },
    },
    colors: ['#3E97FF', '#50CD89', '#7239EA', '#F6C000', '#009EF7', '#FFC700', '#17C653', '#5014D0'],
    dataLabels: {
      enabled: true,
      formatter: (val) => formatCurrency(val as number),
      style: { fontSize: '11px', fontWeight: 600, colors: ['#fff'] },
      offsetX: 5,
    },
    xaxis: {
      labels: {
        formatter: (val) => {
          const n = Number(val);
          if (Math.abs(n) >= 1000) {
            return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', notation: 'compact', maximumFractionDigits: 1 }).format(n);
          }
          return formatCurrency(n);
        },
        style: { colors: 'var(--bs-gray-600)', fontSize: '11px' },
      },
    },
    yaxis: {
      labels: {
        style: { colors: 'var(--bs-gray-700)', fontSize: '12px' },
        maxWidth: 140,
      },
    },
    legend: { show: false },
    tooltip: {
      theme: 'dark',
      custom: ({ dataPointIndex }) => {
        const c = topN[dataPointIndex];
        if (!c) return '';
        return `<div class="px-3 py-2">
          <div class="fw-bold mb-1">${c.name}</div>
          <div>Revenue: ${formatCurrency(c.amount)}</div>
          <div class="text-muted fs-8">${c.invoiceCount} payment${c.invoiceCount !== 1 ? 's' : ''} · ${c.pct.toFixed(1)}% of total</div>
        </div>`;
      },
    },
    grid: {
      borderColor: 'var(--bs-gray-200)',
      strokeDashArray: 4,
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: false } },
    },
    states: {
      hover: { filter: { type: 'darken', value: 0.9 } },
      active: { filter: { type: 'none' } },
    },
  };

  const chartSeries = [{
    name: 'Revenue',
    data: topN.map(c => Math.round(c.amount * 100) / 100),
  }];

  if (customers.length === 0) {
    return (
      <div className="text-center py-10">
        <i className="bi bi-people text-muted fs-2x d-block mb-3" />
        <span className="text-muted fs-6">No customer revenue data in the selected period</span>
      </div>
    );
  }

  return (
    <div className="row g-4">
      <div className="col-12 col-lg-4">
        <div className="d-flex flex-column gap-4">
          <div>
            <span className="text-muted fs-7 d-block mb-1">Total Revenue</span>
            <div className="fs-2 fw-bold text-gray-800">{formatCurrency(totalRevenue)}</div>
            <span className="text-muted fs-8">in period</span>
          </div>
          <div>
            <span className="text-muted fs-7 d-block mb-1">Active Customers</span>
            <div className="fs-3 fw-bold text-primary">{uniqueCustomerCount}</div>
            <span className="text-muted fs-8">with payments</span>
          </div>
          {topCustomer && (
            <div className="pt-2 border-top">
              <span className="text-muted fs-7 d-block mb-1">Top Customer</span>
              <div className="fs-6 fw-bold text-gray-800">{topCustomer.name}</div>
              <div className="d-flex align-items-center gap-2 mt-1">
                <span className="fs-7 fw-semibold text-success">{formatCurrency(topCustomer.amount)}</span>
                <span className="text-muted fs-8">({topCustomer.pct.toFixed(1)}%)</span>
              </div>
            </div>
          )}
          {otherAmount > 0 && (
            <div>
              <span className="text-muted fs-8">
                + {customers.length - 8} other{customers.length - 8 !== 1 ? 's' : ''}: {formatCurrency(otherAmount)}
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="col-12 col-lg-8">
        <Chart
          options={{ ...chartOptions, xaxis: { ...chartOptions.xaxis, categories: topN.map(c => c.name) } }}
          series={chartSeries}
          type="bar"
          height={Math.max(200, topN.length * 36)}
        />
      </div>
    </div>
  );
}

export default TopCustomersView;
